import type { Challenge, CreateChallengeInput } from '@jemmi/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/apiClient';

const CHALLENGES_KEY = ['challenges'] as const;
const CHALLENGE_CAPACITY_KEY = [...CHALLENGES_KEY, 'capacity'] as const;

export type LifecycleChallenge = Challenge & {
  status: 'active' | 'ended';
  endedAt: string | null;
  updatedAt: string | null;
  winnerSubmissionId: string | null;
};

export type UpdateChallengeInput = Partial<Pick<CreateChallengeInput, 'title' | 'brief' | 'category' | 'endsAt'>>;

export function useChallenges() {
  return useQuery({
    queryKey: CHALLENGES_KEY,
    queryFn: () => apiRequest<{ items: LifecycleChallenge[] }>('/challenges'),
  });
}

export function useChallenge(id: string | undefined) {
  return useQuery({
    queryKey: [...CHALLENGES_KEY, id],
    queryFn: () => apiRequest<{ challenge: LifecycleChallenge }>(`/challenges/${id}`),
    enabled: Boolean(id),
  });
}

export function useChallengeCapacity() {
  return useQuery({
    queryKey: CHALLENGE_CAPACITY_KEY,
    queryFn: () => apiRequest<{ activeCount: number; maxActive: number; canCreate: boolean }>('/challenges/capacity'),
    retry: false,
  });
}

export function useCreateChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateChallengeInput) => apiRequest<{ challenge: LifecycleChallenge }>('/challenges', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: ({ challenge }) => {
      queryClient.setQueryData([...CHALLENGES_KEY, challenge.id], { challenge });
      void queryClient.invalidateQueries({ queryKey: CHALLENGES_KEY });
      void queryClient.invalidateQueries({ queryKey: CHALLENGE_CAPACITY_KEY });
    },
  });
}

export function useUpdateChallenge(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: UpdateChallengeInput) => apiRequest<{ challenge: LifecycleChallenge }>(`/challenges/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: ({ challenge }) => {
      queryClient.setQueryData([...CHALLENGES_KEY, id], { challenge });
      void queryClient.invalidateQueries({ queryKey: CHALLENGES_KEY });
    },
  });
}

export function useEndChallenge(id: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiRequest<{ challenge: LifecycleChallenge }>(`/challenges/${id}/end`, { method: 'POST' }),
    onSuccess: ({ challenge }) => {
      queryClient.setQueryData([...CHALLENGES_KEY, id], { challenge });
      void queryClient.invalidateQueries({ queryKey: CHALLENGES_KEY });
      void queryClient.invalidateQueries({ queryKey: CHALLENGE_CAPACITY_KEY });
    },
  });
}

export function useDeleteChallenge() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ deleted: boolean }>(`/challenges/${id}`, { method: 'DELETE' }),
    onSuccess: (_data, id) => {
      queryClient.removeQueries({ queryKey: [...CHALLENGES_KEY, id] });
      void queryClient.invalidateQueries({ queryKey: CHALLENGES_KEY });
      void queryClient.invalidateQueries({ queryKey: CHALLENGE_CAPACITY_KEY });
    },
  });
}
